import React, { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'i18next'
import { Check, ChevronDown, FileUp, Plus, X } from 'lucide-react'

import { Select } from '@/components'
import { EmptySearchResult } from '@/components/EmptySearchResult'
import InputSearch from '@/components/InputSearch'
import { Table } from '@/components/Table'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/features/auth/authStore'
import { useCompanyList } from '@/features/company'
import {
  useDeliverableSetList,
  useDeliverableSetsStore,
} from '@/features/deliverable-set'
import {
  DeliverableSetAction,
  getDeliverableSetListColumns,
} from '@/features/deliverable-set/columns'
import { InviteUsersDialog } from '@/features/deliverable-set/components'
import CancelApprovalRequestDialog from '@/features/deliverable-set/components/CancelApprovalRequestDialog'
import DelegateDialog from '@/features/deliverable-set/components/DelegateDialog'
import DeleteDeliverableSetDialog from '@/features/deliverable-set/components/DeleteDeliverableSetDialog'
import AddDeliverableSetDialog from '@/features/deliverable-set/components/DeliverableSetDialog'
import SendToReviewDialog from '@/features/deliverable-set/components/SendToReviewDialog'
import { formatStatusFilterOptions } from '@/features/deliverable-set/constants'
import { useDocumentStore } from '@/features/document'
import { useProjectList } from '@/features/project'
import { ERoleType } from '@/features/user-management/constants'
import { useCurrentUser } from '@/hooks/useAuth'
import { useDebounce } from '@/hooks/useDebounce'
import {
  ResponseCompany,
  ResponseDeliverableSet,
  ResponseProject,
} from '@/schema'
import { SortOrder } from '@/types/common'
import { cn } from '@/utils'
import {
  DEFAULT_GET_ALL_LIMIT,
  DEFAULT_PAGE,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
  SortDirection,
} from '@/utils/constants'
import {
  buildCommentRoute,
  buildDeliverableSetRoute,
  buildFileUploadRoute,
  ROUTES,
} from '@/utils/routes'

import { ECommentStatus } from '../../features/view-tfl-uploaded'

const DeliverableSetListPage: React.FC = () => {
  const navigate = useNavigate()
  const { setOnlyOpenComments } = useDocumentStore()
  const { data: currentUser } = useCurrentUser()
  const [isStatusPopoverOpen, setIsStatusPopoverOpen] = React.useState(false)

  // Get user roles to check permissions
  const roles = useAuthStore((state) => state.roles)
  const getCompaniesFromRoles = useAuthStore(
    (state) => state.getCompaniesFromRoles
  )
  const getListProjectUserHasAccess = useAuthStore(
    (state) => state.getListProjectUserHasAccess
  )

  const isTeamMember = useMemo(
    () =>
      roles &&
      roles.length > 0 &&
      roles.every((role) => role.role_name === ERoleType.TEAM_MEMBER),
    [roles]
  )

  const isOrgAdminOrProjectLead = useMemo(
    () =>
      roles?.some(
        (role) =>
          role.role_name === ERoleType.ORGANIZATION_ADMIN ||
          role.role_name === ERoleType.PROJECT_LEAD
      ),
    [roles]
  )

  const isOrgAdmin = useMemo(
    () =>
      roles?.some((role) => role.role_name === ERoleType.ORGANIZATION_ADMIN),
    [roles]
  )

  const {
    searchText,
    filters,
    page,
    sort,
    setSearchText,
    setFilter,
    setPage,
    setSort,
    setEditingDeliverableSet,
    setDeletingDeliverableSet,
    setSendToReviewDeliverableSet,
    setCancelApprovalDeliverableSet,
    setDelegateDeliverableSet,
    setDialogOpen,
    reset,
  } = useDeliverableSetsStore()

  // Fetch reference data for dropdowns using React Query
  const { data: { data: companiesData = [] } = {} } = useCompanyList({
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    sort: { column: 'name', direction: SortDirection.ASC },
    enabled: isOrgAdmin,
  })
  // Projects need to be fetched for all users so non-admins can filter by accessible projects
  const { data: { data: projectsData = [] } = {} } = useProjectList({
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    sort: { column: 'project_name', direction: SortDirection.ASC },
  })

  const companyOptions = useMemo(() => {
    if (isOrgAdmin) {
      return [
        { value: 'all', label: t('deliverableSet.filters.all') },
        ...(companiesData || []).map((company: ResponseCompany) => ({
          value: company.id,
          label: company.name,
        })),
      ]
    }
    // For non-admin users, get companies from roles
    const companiesFromRoles = getCompaniesFromRoles()
    return [
      { value: 'all', label: t('deliverableSet.filters.all') },
      ...companiesFromRoles.map((company) => ({
        value: company.id,
        label: company.name,
      })),
    ]
  }, [isOrgAdmin, companiesData, getCompaniesFromRoles])

  const projectOptions = useMemo(() => {
    if (isOrgAdmin) {
      return [
        { value: 'all', label: t('deliverableSet.filters.all') },
        ...(projectsData || []).map((project: ResponseProject) => ({
          value: project.id,
          label: project.project_name,
        })),
      ]
    }
    // For non-admin users, get projects from accessible list
    const accessibleProjectIds = getListProjectUserHasAccess()
    const filteredProjects = (projectsData || []).filter((project) =>
      accessibleProjectIds.includes(project.id)
    )
    return [
      { value: 'all', label: t('deliverableSet.filters.all') },
      ...filteredProjects.map((project) => ({
        value: project.id,
        label: project.project_name,
      })),
    ]
  }, [isOrgAdmin, projectsData, getListProjectUserHasAccess])

  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const searchQuery = useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  useEffect(() => {
    return () => {
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset page when search query changes
  useEffect(() => {
    setPage(0)
  }, [searchQuery, setPage])

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [filters.companyId, filters.projectId, filters.status, setPage])

  // Fetch data using React Query with debounced search
  const {
    data: { data = [], pagination = { page: 0, total_pages: 0 } } = {},
    isLoading,
    isFetching,
  } = useDeliverableSetList({
    search: searchQuery,
    page,
    sort,
    filters,
  })

  // Reset page when no data is returned and not loading
  useEffect(() => {
    if (data?.length === 0 && page > 1 && !isLoading) setPage(page - 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.length])

  // Action handler - routes to appropriate action based on action key
  const handleActionClick = useCallback(
    (action: DeliverableSetAction, item: ResponseDeliverableSet) => {
      switch (action) {
        case 'view-detail':
          // Navigate to detail page
          navigate(buildDeliverableSetRoute(item.id), {
            state: { onlyOpenComments: false },
          })
          break
        case 'edit':
          // Set the item to edit in the store and open the dialog
          setEditingDeliverableSet(item)
          break
        case 'delete':
          // Set the item to delete and open the delete dialog
          setDeletingDeliverableSet(item)
          break
        case 'view-files-with-open-comments':
          // Navigate to detail page with filter for files with open comments
          navigate(buildDeliverableSetRoute(item.id), {
            state: { onlyOpenComments: true },
          })
          break
        case 'view-total-open-comments': {
          const route = buildCommentRoute({
            deliverableSetId: item.id,
            status: ECommentStatus.OPEN,
          })
          navigate(route.path, { state: route.state })
          break
        }
        case 'sendApproval':
          setSendToReviewDeliverableSet(item)
          break
        case 'cancelApproval':
          setCancelApprovalDeliverableSet(item)
          break
        case 'delegateReview':
          setDelegateDeliverableSet(item)
          break
        default:
          // Unknown action - this should never happen with proper typing
          break
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      navigate,
      setEditingDeliverableSet,
      setDeletingDeliverableSet,
      setOnlyOpenComments,
      setSendToReviewDeliverableSet,
      setCancelApprovalDeliverableSet,
    ]
  )

  // Handle row click - navigate to detail page
  // Skip if click originated from a button (e.g., action buttons in columns)
  const handleRowClick = useCallback(
    (row: ResponseDeliverableSet, event?: React.MouseEvent) => {
      // Check if the click originated from a button or interactive element
      if (event) {
        const target = event.target as HTMLElement
        // If the click target is a button or inside a button, ignore the row click
        if (target.closest('button')) {
          return
        }
      }
      handleActionClick('view-detail', row)
    },
    [handleActionClick]
  )

  // Get columns from constants
  const columns = useMemo(
    () =>
      getDeliverableSetListColumns({
        handleActionClick,
        t,
        isFetching,
        currentUserId: currentUser?.id,
        isTeamMember,
        isOrgAdminOrProjectLead,
      }),
    [
      currentUser?.id,
      handleActionClick,
      isFetching,
      isOrgAdminOrProjectLead,
      isTeamMember,
    ]
  )

  // Handle sort change
  const handleSortChange = useCallback(
    (colId: keyof ResponseDeliverableSet | null, dir: SortOrder) => {
      setSort({
        column: colId,
        direction: dir,
      })
    },
    [setSort]
  )

  const noDataMessage = useMemo(() => {
    if (searchQuery) {
      return <EmptySearchResult translationKey="deliverableSet" />
    }
    return null
  }, [searchQuery])

  const statusButtonLabel = useMemo(() => {
    if (!filters.status || filters.status.length === 0) {
      return t('deliverableSet.filters.status')
    }
    const firstOption = formatStatusFilterOptions().find(
      (option) => option.value === filters?.status?.[0]
    )

    let firstLabel = ''
    if (firstOption) {
      const label = firstOption.label
      if (typeof label === 'string') {
        firstLabel = label
      }
    }

    if (filters.status.length === 1) {
      return firstLabel
    }
    return `${firstLabel}, +${filters.status.length - 1}`
  }, [filters.status])

  return (
    <div className="flex flex-col space-y-6">
      {/* MARK: Page Title */}
      <div className="flex justify-between flex-shrink-0">
        <div>
          <h1 className="m-0 mb-1 font-semibold text-display-xs">
            {t('deliverableSet.page.title')}
          </h1>
          <p className="m-0 text-md text-muted-foreground">
            {t('deliverableSet.page.description')}
          </p>
        </div>
        {!isTeamMember && (
          <div className="flex gap-2">
            <Button
              variant="outlined"
              className="flex items-center gap-1 px-3.5 py-2.5 h-fit text-secondary-700 min-w-[120px]"
              leadingIcon={
                <FileUp
                  className="text-foreground-fg-quaternary-400"
                  size={20}
                />
              }
              onClick={() =>
                navigate(buildFileUploadRoute(), {
                  state: { from: ROUTES.DELIVERABLE_SET },
                })
              }
            >
              {t('deliverableSet.actions.importFile')}
            </Button>
            <Button
              color="primary"
              className="flex items-center gap-1 px-3.5 py-2.5 border border-solid border-transparent h-fit text-sm min-w-[120px]"
              leadingIcon={<Plus size={20} />}
              onClick={() => setDialogOpen(true)}
            >
              {t('deliverableSet.actions.addNew')}
            </Button>
          </div>
        )}
      </div>

      {/* MARK: Search */}
      <div className="flex flex-wrap justify-between flex-shrink-0 gap-3">
        <InputSearch
          searchText={searchText}
          setSearchText={setSearchText}
          placeholder={t('deliverableSet.filters.search')}
        />

        {/* MARK: Filters */}
        <div className="flex flex-wrap gap-3">
          <Select
            value={filters.companyId || ''}
            onChange={(value) =>
              setFilter({ companyId: value === 'all' ? null : value })
            }
            placeholder={t('deliverableSet.filters.company')}
            searchable
            clearable
            options={companyOptions}
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
            maxHeight={600}
          />

          <Select
            value={filters.projectId || ''}
            onChange={(value) =>
              setFilter({
                projectId: value === 'all' ? null : value,
              })
            }
            placeholder={t('deliverableSet.filters.project')}
            searchable
            clearable
            options={projectOptions}
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
            maxHeight={600}
          />

          <Popover
            open={isStatusPopoverOpen}
            onOpenChange={setIsStatusPopoverOpen}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className="relative inline-flex items-center justify-between h-10 px-3.5 py-2.5 text-sm font-medium text-primary-900 border border-solid border-[--Colors-Gray-light-300] bg-white rounded-lg shadow-xs hover:bg-gray-50 focus:outline-none focus:border-blue-500 hover:border-blue-400 focus:ring-1 focus:ring-blue-500 cursor-pointer"
              >
                <span className="truncate">{statusButtonLabel}</span>
                {filters.status && filters.status.length > 0 ? (
                  <X
                    size={16}
                    className="ml-2 text-gray-500 hover:text-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      setFilter({ status: null })
                      setIsStatusPopoverOpen(false)
                    }}
                  />
                ) : (
                  <ChevronDown size={16} className="ml-2 text-gray-500" />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="sm:max-w-[140px] p-0"
              side="bottom"
              align="end"
            >
              <div className="py-1 px-1.5 space-y-0.5">
                {/* Individual status options */}
                {formatStatusFilterOptions().map((option) => {
                  const isSelected = filters.status?.includes(option.value)
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={cn(
                        'p-sm font-medium text-sm cursor-pointer rounded-sm hover:bg-primary-hover flex gap-2 items-center justify-between w-full text-left bg-transparent border-none',
                        isSelected ? 'bg-primary-hover' : ''
                      )}
                      onClick={() => {
                        const currentStatus = filters.status || []
                        if (isSelected) {
                          const newStatus = currentStatus.filter(
                            (s) => s !== option.value
                          )
                          setFilter({
                            status: newStatus.length > 0 ? newStatus : null,
                          })
                        } else {
                          setFilter({
                            status: [...currentStatus, option.value],
                          })
                        }
                      }}
                    >
                      <span>{option.label}</span>
                      {isSelected && (
                        <Check
                          size={16}
                          className="text-foreground-fg-brand-primary-600"
                        />
                      )}
                    </button>
                  )
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* MARK: Paginated Table */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <Table
          columns={columns}
          data={data ?? []}
          page={(pagination?.page ?? DEFAULT_PAGE) - 1}
          totalPages={pagination?.total_pages ?? 0}
          onPageChange={setPage}
          isLoading={isLoading}
          noDataMessage={noDataMessage}
          orderBy={sort.column}
          order={sort.direction}
          onSortChange={handleSortChange}
          onRowClick={handleRowClick}
          roundedTop
        />
      </div>

      {/* Dialog components */}
      <AddDeliverableSetDialog />
      <DeleteDeliverableSetDialog />
      <SendToReviewDialog />
      <CancelApprovalRequestDialog />
      <DelegateDialog />
      <InviteUsersDialog />
    </div>
  )
}

export default DeliverableSetListPage
